import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Field, change } from 'redux-form';
import { FilterField } from '../FiltersCard';
import { DateTimePicker } from '..';
import Utils from '../../services/Utils';
import {
    allowsCinemaWeekPresets,
    custom_seed_from_preset,
    resolve_date_range_preset,
} from './dateRangePresets';
import { readUserInstancePrefString, writeUserInstancePrefString } from '../../services/kronosPrefs/userInstancePrefs';

function read_storage(key, fallback) {
    if (typeof window === 'undefined' || !window.localStorage) return fallback;
    try {
        const v = window.localStorage.getItem(key);
        return v || fallback;
    } catch (_e) {
        return fallback;
    }
}

function write_storage(key, value) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(key, value);
    } catch (_e) {
        // ignore
    }
}

/**
 * UI-only control: preset select + computed dates (readonly) + custom DateTimePickers.
 * Parent must handle "apply" by including `preset` in its equality key / apply logic.
 */
export default function DateRangePresetControl({
    storageKey,
    /** redux-form name — used to seed start/end when switching from a preset to custom. */
    form,
    /** When the preset has no end (`now`), seed end to this date at 23:59 (e.g. max loaded row). */
    openEndFallback,
    /** Dot path under user↔instance prefs (e.g. `customers.purchaseDatePreset`). */
    instancePrefStringPath,
    presetDefault,
    preset,
    onPresetChange,
    showCinemaWeek,
    uiLang,
    withTime,
    startField,
    endField,
    startLabel,
    endLabel,
    selectLabel,
    colLg,
    colMd,
    colXs,
    /** Optional Field component (e.g. EngineLogsDateField) instead of default DateTimePicker */
    dateFieldComponent,
    compact,
    /** When true, do not read/write `storageKey` in localStorage (preset comes from Redux, etc.) */
    disableLocalStorage,
    /** When true, switching to custom only changes the preset — no form/Redux date seeding. */
    disableCustomSeed,
}) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const effective_ui_lang = uiLang ?? (i18n.resolvedLanguage || i18n.language);
    const setting = useSelector((s) => s.settings?.setting);
    const hasOwnMovies = useSelector((s) => s.settings?.hasOwnMovies);
    const instance_id = useSelector((s) => (
        s.auth?.user?.tkt_instance?.id != null ? String(s.auth.user.tkt_instance.id) : 'none'
    ));
    const user_id = useSelector((s) => (
        s.auth?.user?.id != null ? String(s.auth.user.id) : null
    ));
    const allowCinemaWeek = showCinemaWeek && allowsCinemaWeekPresets(setting, hasOwnMovies);

    const local_default = presetDefault || 'today';
    const storage_key = storageKey || '';
    const internal_ref = useRef(
        disableLocalStorage
            ? (preset ?? local_default)
            : (instancePrefStringPath
                ? (user_id
                    ? (readUserInstancePrefString(user_id, instance_id, instancePrefStringPath) || local_default)
                    : local_default)
                : read_storage(storage_key, local_default))
    );
    const [internal, setInternal] = useState(() => preset ?? internal_ref.current);
    const effective_preset = preset ?? internal;

    const is_custom = effective_preset === 'custom';
    const resolved = useMemo(() => resolve_date_range_preset(effective_preset), [effective_preset]);
    const resolved_start = resolved?.start ? resolved.start.toISOString() : null;
    const resolved_end = resolved?.end ? resolved.end.toISOString() : null;

    const set_preset = useCallback((next) => {
        const prev = preset ?? internal_ref.current;
        internal_ref.current = next;
        if (!disableLocalStorage) {
            if (instancePrefStringPath) {
                if (user_id) {
                    writeUserInstancePrefString(user_id, instance_id, instancePrefStringPath, next);
                }
            } else if (storage_key) {
                write_storage(storage_key, next);
            }
        }
        let seed = null;
        if (next === 'custom' && prev !== 'custom' && form && !disableCustomSeed) {
            seed = custom_seed_from_preset(prev, undefined, { open_end_fallback: openEndFallback });
            if (seed) {
                dispatch(change(form, startField, seed.start));
                dispatch(change(form, endField, seed.end));
            }
        }
        if (typeof onPresetChange === 'function') onPresetChange(next, seed);
        setInternal(next);
    }, [
        disableLocalStorage,
        disableCustomSeed,
        dispatch,
        endField,
        form,
        instancePrefStringPath,
        instance_id,
        onPresetChange,
        openEndFallback,
        preset,
        startField,
        storage_key,
        user_id,
    ]);

    const on_select_change = useCallback((e) => {
        e.stopPropagation();
        set_preset(e.target.value);
    }, [set_preset]);

    const activate_custom = useCallback((e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (!is_custom) set_preset('custom');
    }, [is_custom, set_preset]);

    const readonly_key_down = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate_custom(e);
        }
    }, [activate_custom]);

    const date_format = withTime ? 'LLL' : 'LL';
    const Picker = dateFieldComponent || DateTimePicker;
    const nowLabelKey = withTime
        ? 'statstypes.form.date_preset_now'
        : 'statstypes.form.date_preset_now_day_granularity';

    return (
        <>
            <FilterField label={selectLabel} lg={colLg} md={colMd} xs={colXs}>
                <Input
                    type="select"
                    className="custom-select"
                    value={effective_preset}
                    onChange={on_select_change}
                    onClick={(e) => e.stopPropagation()}
                >
                    <option value="custom">{t('statstypes.form.date_preset_custom')}</option>
                    <option disabled>{t('statstypes.form.date_preset_group_floating')}</option>
                    <option value="now">{t(nowLabelKey)}</option>
                    <option value="last7">{t('statstypes.form.date_preset_last7')}</option>
                    <option value="last30">{t('statstypes.form.date_preset_last30')}</option>
                    <option disabled>{t('statstypes.form.date_preset_group_calendar')}</option>
                    <option value="today">{t('statstypes.form.date_preset_today_short')}</option>
                    <option value="yesterday">{t('statstypes.form.date_preset_yesterday')}</option>
                    <option value="this_week">{t('statstypes.form.date_preset_this_week')}</option>
                    {allowCinemaWeek && (
                        <option value="this_cinema_week">{t('statstypes.form.date_preset_this_cinema_week')}</option>
                    )}
                    <option value="this_month">{t('statstypes.form.date_preset_this_month')}</option>
                    <option value="this_year">{t('statstypes.form.date_preset_this_year')}</option>
                    <option value="last_week">{t('statstypes.form.date_preset_last_week')}</option>
                    {allowCinemaWeek && (
                        <option value="last_cinema_week">{t('statstypes.form.date_preset_last_cinema_week')}</option>
                    )}
                    <option value="last_month">{t('statstypes.form.date_preset_last_month')}</option>
                    <option value="last_year">{t('statstypes.form.date_preset_last_year')}</option>
                </Input>
            </FilterField>

            {!is_custom ? (
                <>
                    <FilterField label={startLabel} lg={colLg} md={colMd} xs={colXs}>
                        <input
                            type="text"
                            className="form-control"
                            readOnly
                            role="button"
                            tabIndex={0}
                            value={resolved_start ? Utils.formatLocalizedDateTime(resolved_start, effective_ui_lang, date_format) : ''}
                            onMouseDown={activate_custom}
                            onKeyDown={readonly_key_down}
                            style={{ opacity: 0.75 }}
                        />
                    </FilterField>
                    <FilterField label={endLabel} lg={colLg} md={colMd} xs={colXs}>
                        <input
                            type="text"
                            className="form-control"
                            readOnly
                            role="button"
                            tabIndex={0}
                            value={resolved_end ? Utils.formatLocalizedDateTime(resolved_end, effective_ui_lang, date_format) : ''}
                            onMouseDown={activate_custom}
                            onKeyDown={readonly_key_down}
                            style={{ opacity: 0.75 }}
                        />
                    </FilterField>
                </>
            ) : (
                <>
                    <FilterField label={startLabel} wrap={false} lg={colLg} md={colMd} xs={colXs}>
                        <Field
                            component={Picker}
                            name={startField}
                            maxBoundField={endField}
                            prependLabel={startLabel}
                            timeFormat={withTime ? undefined : false}
                            compact={compact}
                            destroyOnUnmount={false}
                        />
                    </FilterField>
                    <FilterField label={endLabel} wrap={false} lg={colLg} md={colMd} xs={colXs}>
                        <Field
                            component={Picker}
                            name={endField}
                            minBoundField={startField}
                            endOfDayIfMidnight
                            openAt={openEndFallback || undefined}
                            prependLabel={endLabel}
                            timeFormat={withTime ? undefined : false}
                            compact={compact}
                            destroyOnUnmount={false}
                        />
                    </FilterField>
                </>
            )}
        </>
    );
}

DateRangePresetControl.propTypes = {
    storageKey: PropTypes.string,
    form: PropTypes.string,
    openEndFallback: PropTypes.string,
    instancePrefStringPath: PropTypes.string,
    presetDefault: PropTypes.string,
    preset: PropTypes.string,
    onPresetChange: PropTypes.func,
    showCinemaWeek: PropTypes.bool,
    uiLang: PropTypes.string,
    withTime: PropTypes.bool,
    startField: PropTypes.string.isRequired,
    endField: PropTypes.string.isRequired,
    startLabel: PropTypes.node.isRequired,
    endLabel: PropTypes.node.isRequired,
    selectLabel: PropTypes.node.isRequired,
    colLg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    colMd: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    colXs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dateFieldComponent: PropTypes.elementType,
    compact: PropTypes.bool,
    disableLocalStorage: PropTypes.bool,
    disableCustomSeed: PropTypes.bool,
};

DateRangePresetControl.defaultProps = {
    storageKey: '',
    form: undefined,
    openEndFallback: undefined,
    presetDefault: 'today',
    preset: undefined,
    onPresetChange: undefined,
    showCinemaWeek: true,
    uiLang: undefined,
    withTime: true,
    colLg: 4,
    colMd: 6,
    colXs: 12,
    dateFieldComponent: undefined,
    compact: false,
    disableLocalStorage: false,
    disableCustomSeed: true,
};
